import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { Round } from '../data/types';
import PlayView from '../play/PlayView';
import { ensureSeed, shuffleWithSeed } from '../play/shuffle';

/* Endless Mode: /real-or-fake/modes/endless
 *
 * The whole pool in one payload, played with no cap. He stops when he wants to; if he goes all
 * the way, running the pool dry lands on the ordinary wrap screen rather than anything that
 * looks like an error.
 *
 * ── The seed ──
 * When the pool is set to shuffled, the order is derived from a seed minted once and written
 * into the URL as ?s=. It has to be in the URL because play progress is, and progress is an
 * INDEX: reshuffle on a refresh and index 7 comes back as a different headline, quietly
 * attaching the verdicts he already recorded to the wrong ones. Seed in the URL means the order
 * survives exactly as far as the progress does.
 */

export default function ModesEndless() {
  const [round, setRound] = useState<Round | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await api.endlessPlay();
        if (!cancelled) setRound(loaded);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the endless queue.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* Ordered once, from the seed — NOT on every render. A re-order between renders would move
     the headline out from under the current index mid-session. */
  const ordered = useMemo(() => {
    if (!round) return null;
    if (round.playOrder !== 'shuffled') return round;
    return { ...round, items: shuffleWithSeed(round.items, ensureSeed()) };
  }, [round]);

  if (error) {
    return (
      <div className="centred">
        <div className="round-title">Real or Fake?</div>
        <div className="big">Could not start Endless Mode</div>
        <div className="sub">{error}</div>
        <Link className="btn primary" to="/modes" style={{ textDecoration: 'none' }}>Back</Link>
      </div>
    );
  }

  if (!ordered) return <div className="centred"><div className="big">Loading…</div></div>;

  if (ordered.items.length === 0) {
    return (
      <div className="centred" data-testid="endless-empty">
        <div className="round-title">Real or Fake?</div>
        <div className="big">The endless queue is empty</div>
        {/* Names neither the people nor the list, and both for the same reason: this is the one
            screen here that can end up in frame, and everything it named has since changed.
            "Team" rather than Alyssa because whoever stocks it can change — and no list name at
            all since the 2026-08-21 split, which left this pointing at an "Endless Queue" the
            builder no longer has. Two banks feed this mode now; he is not meant to know that,
            and there is nothing here he would do with the answer anyway. */}
        <div className="sub">Ask Team to add more headlines.</div>
        <Link className="btn primary" to="/modes" style={{ textDecoration: 'none' }}>Back</Link>
      </div>
    );
  }

  /* Recorded like any other run. The pool has no lifecycle to advance — markRecorded refuses
     anything that is not a series — but "he got through 30 of them on Tuesday" is worth having. */
  return <PlayView round={ordered} endless session={{ source: 'modes', key: ordered.id }} />;
}
